import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MetricsData {
  mrr: number;
  mrrChange: number;
  arr: number;
  arrChange: number;
  churn: number;
  churnChange: number;
  totalRevenue: number;
  revenueChange: number;
  cac: number;
  ltv: number;
  averageTicket: number;
  totalCustomers: number;
}

interface TimeSeriesData {
  month: string;
  revenue?: number;
  churn?: number;
  target?: number;
}

export async function fetchMetricsData(
  startDate: Date,
  endDate: Date,
  productId?: string
): Promise<MetricsData> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: userRole } = await supabase
    .from('user_roles')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single();

  if (!userRole) throw new Error('No tenant found');

  const startDateStr = startOfDay(startDate).toISOString();
  const endDateStr = endOfDay(endDate).toISOString();

  // Query para métricas do período atual
  let query = supabase
    .from('metrics')
    .select('*')
    .eq('tenant_id', userRole.tenant_id)
    .gte('period_start', startDateStr)
    .lte('period_end', endDateStr);

  if (productId) {
    query = query.eq('product_id', productId);
  }

  const { data: metrics, error } = await query;
  if (error) throw error;

  // Calcular período anterior
  const periodDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const previousStartDate = new Date(startDate);
  previousStartDate.setDate(previousStartDate.getDate() - periodDays);
  const previousEndDate = new Date(endDate);
  previousEndDate.setDate(previousEndDate.getDate() - periodDays);

  // Query para métricas do período anterior
  let previousQuery = supabase
    .from('metrics')
    .select('*')
    .eq('tenant_id', userRole.tenant_id)
    .gte('period_start', startOfDay(previousStartDate).toISOString())
    .lte('period_end', endOfDay(previousEndDate).toISOString());

  if (productId) {
    previousQuery = previousQuery.eq('product_id', productId);
  }

  const { data: previousMetrics } = await previousQuery;

  // Buscar total de clientes
  let customersQuery = supabase
    .from('customers')
    .select('id', { count: 'exact' })
    .eq('tenant_id', userRole.tenant_id)
    .eq('is_active', true);

  if (productId) {
    customersQuery = customersQuery.eq('product_id', productId);
  }

  const { count: totalCustomers } = await customersQuery;

  // MRR usando transações com normalização
  const { data: activeSubscriptions } = await supabase
    .from('transactions')
    .select('mrr_normalized, coverage_months')
    .eq('tenant_id', userRole.tenant_id)
    .eq('payment_status', 'paid')
    .lte('payment_date', endDateStr)
    .gte('period_end_date', startDateStr);
  
  const mrr = activeSubscriptions?.reduce((sum, t) => 
    sum + (t.mrr_normalized || 0), 0
  ) || 0;
  const arr = mrr * 12;

  // Calcular métricas antigas (mantém compatibilidade)
  const churnMetrics = metrics?.filter((m) => m.metric_type === 'churn') || [];
  const revenueMetrics = metrics?.filter((m) => m.metric_type === 'revenue') || [];
  const cacMetrics = metrics?.filter((m) => m.metric_type === 'cac') || [];
  const ltvMetrics = metrics?.filter((m) => m.metric_type === 'ltv') || [];

  const churn =
    churnMetrics.length > 0
      ? churnMetrics.reduce((sum, m) => sum + Number(m.value), 0) / churnMetrics.length
      : 0;

  const totalRevenue = revenueMetrics.reduce((sum, m) => sum + Number(m.value), 0);

  const cac =
    cacMetrics.length > 0
      ? cacMetrics.reduce((sum, m) => sum + Number(m.value), 0) / cacMetrics.length
      : 0;

  const ltv =
    ltvMetrics.length > 0
      ? ltvMetrics.reduce((sum, m) => sum + Number(m.value), 0) / ltvMetrics.length
      : 0;

  const averageTicket = totalCustomers ? totalRevenue / totalCustomers : 0;

  // Calcular métricas do período anterior
  const previousMrrMetrics =
    previousMetrics?.filter((m) => m.metric_type === 'mrr') || [];
  const previousChurnMetrics =
    previousMetrics?.filter((m) => m.metric_type === 'churn') || [];
  const previousRevenueMetrics =
    previousMetrics?.filter((m) => m.metric_type === 'revenue') || [];

  const previousMrr = previousMrrMetrics.reduce((sum, m) => sum + Number(m.value), 0);
  const previousArr = previousMrr * 12;
  const previousChurn =
    previousChurnMetrics.length > 0
      ? previousChurnMetrics.reduce((sum, m) => sum + Number(m.value), 0) /
        previousChurnMetrics.length
      : 0;
  const previousRevenue = previousRevenueMetrics.reduce(
    (sum, m) => sum + Number(m.value),
    0
  );

  // Calcular variações
  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  return {
    mrr,
    mrrChange: calculateChange(mrr, previousMrr),
    arr,
    arrChange: calculateChange(arr, previousArr),
    churn,
    churnChange: calculateChange(churn, previousChurn),
    totalRevenue,
    revenueChange: calculateChange(totalRevenue, previousRevenue),
    cac,
    ltv,
    averageTicket,
    totalCustomers: totalCustomers || 0,
  };
}

export async function fetchTimeSeriesData(
  startDate: Date,
  endDate: Date,
  productId?: string
): Promise<{ revenueData: TimeSeriesData[]; churnData: TimeSeriesData[] }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: userRole } = await supabase
    .from('user_roles')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single();

  if (!userRole) throw new Error('No tenant found');

  const startDateStr = startOfDay(startDate).toISOString();
  const endDateStr = endOfDay(endDate).toISOString();

  // Query para métricas do período
  let query = supabase
    .from('metrics')
    .select('*')
    .eq('tenant_id', userRole.tenant_id)
    .gte('period_start', startDateStr)
    .lte('period_end', endDateStr)
    .order('period_start', { ascending: true });

  if (productId) {
    query = query.eq('product_id', productId);
  }

  const { data: metrics, error } = await query;
  if (error) throw error;

  // Agrupar métricas por mês
  const groupedByMonth = new Map<
    string,
    { revenue: number[]; churn: number[] }
  >();

  metrics?.forEach((metric) => {
    const monthKey = format(new Date(metric.period_start), 'MMM', { locale: ptBR });

    if (!groupedByMonth.has(monthKey)) {
      groupedByMonth.set(monthKey, { revenue: [], churn: [] });
    }

    const group = groupedByMonth.get(monthKey)!;

    if (metric.metric_type === 'revenue' || metric.metric_type === 'mrr') {
      group.revenue.push(Number(metric.value));
    } else if (metric.metric_type === 'churn') {
      group.churn.push(Number(metric.value));
    }
  });

  // Converter para array de dados do gráfico
  const revenueData: TimeSeriesData[] = [];
  const churnData: TimeSeriesData[] = [];

  groupedByMonth.forEach((values, month) => {
    const avgRevenue =
      values.revenue.length > 0
        ? values.revenue.reduce((a, b) => a + b, 0) / values.revenue.length
        : 0;

    const avgChurn =
      values.churn.length > 0
        ? values.churn.reduce((a, b) => a + b, 0) / values.churn.length
        : 0;

    revenueData.push({
      month,
      revenue: avgRevenue,
      target: 0, // Pode ser buscado das business_goals se necessário
    });

    churnData.push({
      month,
      churn: avgChurn,
      target: 0, // Pode ser buscado das business_goals se necessário
    });
  });

  return { revenueData, churnData };
}
